import React, { FocusEventHandler, useMemo, useState } from 'react';
import StyledInput from '../../custom/StyledInput';
import DatePicker from '../../custom/DatePicker';
import { StyledSelect } from '../../custom/StyledSelect';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { LimitCheckPeriod } from '../../../utils/DateConverter';
import { selectPersonaInformation, updatePersonaInformation } from '../../../app/slices/persona.slice';
import { Box } from '@mui/material';
import { SubmitButton } from '../shared/SubmitButton';
import { Header } from './Header';
import { useAppTranslation } from '../../../i18n';
import { changeStep } from '../../../app/slices/step.slice';
import { AppEnv, Env } from '../../../constants/env';
import { useStepFeatureSettings } from './useStepFeatureSettings';
import { FirstnameSchema, LastnameSchema } from './schema';
import { Country } from '../../../constants/country-phone-number';
import { format, isValid, parse, sub } from 'date-fns';

type PersonaAttribute = 'firstname' | 'lastname' | 'dateOfBirth' | 'country';

const COUNTRY_OPTIONS = ['AT', 'DE'] as const;

const PersonaInformationInputStep = () => {
	const dispatch = useAppDispatch();
	const { t } = useAppTranslation();

	const persona = useAppSelector(selectPersonaInformation);

	const {
		isEditingPermitted,
		isDateOfBirthShown,
		isCountryShown,
		isDateOfBirthRequired,
		isPlaceOfResidenceRequired
	} = useStepFeatureSettings();

	const [firstname, setFirstname] = useState(persona.firstName || '');
	const [lastname, setLastname] = useState(persona.lastName || '');
	const [country, setCountry] = useState(persona.country || '');
	const [dateOfBirth, setDateOfBirth] = useState(persona.dateOfBirth ? getFormattedDate(persona.dateOfBirth) : null);

	const [errors, setErrors] = useState({
		firstname: !persona.firstName,
		lastname: !persona.lastName,
		country: !persona.country && isPlaceOfResidenceRequired,
		dateOfBirth: !persona.dateOfBirth && isDateOfBirthRequired
	});

	const [touched, setTouched] = useState({
		firstname: false,
		lastname: false,
		country: false,
		dateOfBirth: false
	});

	// for testing purpose
	if (getCurrentEnv() === AppEnv.DEV && firstname === 'error') {
		throw new Error('test');
	}

	const handleChangeFirstName = (value: string): void => {
		setFirstname(value);
		const validation = FirstnameSchema.safeParse(value);
		const hasError = !validation.success;
		setErrors({
			...errors,
			firstname: hasError
		});
	};

	const handleChangeLastName = (value: string): void => {
		setLastname(value);
		const validation = LastnameSchema.safeParse(value);
		const hasError = !validation.success;
		setErrors({
			...errors,
			lastname: hasError
		});
	};

	const handleChangeCountry = (country: string) => {
		setCountry(country);
		setErrors({
			...errors,
			country: false
		});
	};

	const handleChangeDateOfBirth = (date: Date | null): void => {
		if (date === null) {
			setDateOfBirth(date);
			return setErrors({
				...errors,
				dateOfBirth: isDateOfBirthRequired
			});
		}

		const isValidDate = isValid(date);
		const hasError = !isValidDate;
		setDateOfBirth(date);
		setErrors({
			...errors,
			dateOfBirth: hasError
		});
	};

	const createBlurHandler = (attribute: PersonaAttribute): FocusEventHandler => {
		return () => {
			setTouched({
				...touched,
				[attribute]: true
			});
		};
	};

	const handleSubmit = () => {
		dispatch(
			updatePersonaInformation({
				firstName: firstname,
				lastName: lastname,
				dateOfBirth: dateOfBirth ? format(dateOfBirth, 'dd.MM.yyyy') : '',
				country: country as Country
			})
		);

		dispatch(changeStep('PERSONA_CONSENT_INPUT_STEP'));
	};

	const countryOptions = COUNTRY_OPTIONS.map((country) => ({
		value: country,
		label: t(`countries.${country}`)
	}));

	const isSubmitButtonDisabled = useMemo(() => {
		return Object.values(errors).filter((value) => value).length > 0;
	}, [errors]);

	const maxDate = sub(new Date(), {
		[LimitCheckPeriod.TIME_METRIC as 'years' | 'months' | 'days']: LimitCheckPeriod.MAX_DATE
	});

	// inputs are disabled if editing is not permitted and persona info is provided
	const isEditingFirstnameDisabled = !isEditingPermitted && !!persona.firstName;
	const isEditingLastnameDisabled = !isEditingPermitted && !!persona.lastName;
	const isEditingCountryDisabled = !isEditingPermitted && !!persona.country;
	const isEditingDateOfBirthDisabled = !isEditingPermitted && !!persona.dateOfBirth;

	const firstnamePlaceholder = `${t(`personal-info-page.inputs.firstname`)} *`;
	const lastnamePlaceholder = `${t(`personal-info-page.inputs.lastname`)} *`;
	const countryPlaceholder = `${t(`personal-info-page.inputs.country`)} ${isPlaceOfResidenceRequired ? '*' : ''}`;
	const dateOfBirthPlaceholder = `${t(`personal-info-page.inputs.dateOfBirth`)} ${isDateOfBirthRequired ? '*' : ''}`;
	return (
		<Box id="persona-information-input-step">
			<Header />
			<Box className="personal-info-step" sx={{ pt: 3, pb: 3 }}>
				<StyledInput
					id="name"
					value={firstname}
					required={true}
					placeholder={firstnamePlaceholder}
					disabled={isEditingFirstnameDisabled}
					error={touched.firstname && errors.firstname}
					onChange={handleChangeFirstName}
					onBlur={(e) => {
						createBlurHandler('firstname')(e);
						handleChangeFirstName(firstname.trim());
					}}
				/>
				<StyledInput
					id="lastname"
					value={lastname}
					required={true}
					placeholder={lastnamePlaceholder}
					disabled={isEditingLastnameDisabled}
					error={touched.lastname && errors.lastname}
					onChange={handleChangeLastName}
					onBlur={(e) => {
						createBlurHandler('lastname')(e);
						handleChangeLastName(lastname.trim());
					}}
				/>
				{isCountryShown && (
					<StyledSelect
						id="select-country"
						value={country}
						options={countryOptions}
						label={countryPlaceholder}
						disabled={isEditingCountryDisabled}
						onChange={handleChangeCountry}
					/>
				)}
				{isDateOfBirthShown && (
					<DatePicker
						id="date-picker"
						value={dateOfBirth}
						placeholder={dateOfBirthPlaceholder}
						maxDate={maxDate}
						error={touched.dateOfBirth && errors.dateOfBirth}
						disabled={isEditingDateOfBirthDisabled}
						onChange={handleChangeDateOfBirth}
						onBlur={createBlurHandler('dateOfBirth')}
					/>
				)}
			</Box>
			<Box sx={{ mb: 5 }}>
				<SubmitButton disabled={isSubmitButtonDisabled} submitting={false} onSubmit={handleSubmit} />
			</Box>
		</Box>
	);
};

export default PersonaInformationInputStep;

const getCurrentEnv = () => {
	return process.env[Env.REACT_APP_ENV] || AppEnv.LIVE;
};

const getFormattedDate = (dateOfBirth: string) => {
	const parsedDate = parse(dateOfBirth, 'dd.MM.yyyy', new Date());

	if (!isValid(parsedDate)) {
		return null;
	}

	return parsedDate;
};
